import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Download, Check, Loader2, ChevronLeft, ChevronRight, Clock, X, Hash } from "lucide-react";
import AdminShell from "./AdminShell";
import { cjSearch } from "../../lib/api";

const BACKOFF = [30, 60, 120]; // secondes, escalade sur rate-limit CJ répété

export default function CjCatalog() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [sku, setSku] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [err, setErr] = useState("");
  const [countdown, setCountdown] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);
  const pageSize = 20;

  useEffect(() => () => clearInterval(timerRef.current), []);

  const scheduleRetry = (seconds, targetPage) => {
    clearInterval(timerRef.current);
    setCountdown(seconds);
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(timerRef.current);
          run(targetPage, true);
          return null;
        }
        return c - 1;
      });
    }, 1000);
  };

  const cancelRetry = () => {
    clearInterval(timerRef.current);
    setCountdown(null);
    attemptsRef.current = 0;
  };

  const run = async (p = 1, isAuto = false) => {
    if (!isAuto) { attemptsRef.current = 0; clearInterval(timerRef.current); setCountdown(null); }
    setBusy(true); setErr("");
    try {
      const res = await cjSearch({ keyword, sku: sku.trim() || undefined, page_num: p, page_size: pageSize });
      setData(res); setPage(p); setFromCache(!!res.from_cache);
      attemptsRef.current = 0; setCountdown(null);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429 && detail?.code === "CJ_RATE_LIMIT") {
        const secs = BACKOFF[Math.min(attemptsRef.current, BACKOFF.length - 1)];
        attemptsRef.current += 1;
        scheduleRetry(secs, p);
      } else if (status === 429 && detail?.code === "LOCAL_RATE_LIMIT") {
        toast.info(`Patientez ${detail.retry_after}s avant une nouvelle recherche`);
      } else {
        const msg = typeof detail === "string" ? detail : (detail?.message || "Erreur de recherche");
        setErr(msg); setData(null);
        toast.error("Recherche CJ impossible");
      }
    }
    setBusy(false);
  };

  const submit = (e) => { e.preventDefault(); run(1); };
  const goImport = (p) => navigate(`/admin/products/import?pid=${encodeURIComponent(p.pid)}&name=${encodeURIComponent(p.name_en)}`);
  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

  return (
    <AdminShell title="Catalogue CJdropshipping">
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 mb-2" data-testid="cj-search-form">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ardoise/50" />
          <input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="Rechercher un produit (ex: dress, jewelry...)"
            className="w-full border border-dore/30 pl-9 pr-4 py-3 text-sm outline-none focus:border-dore" data-testid="cj-search-input" />
        </div>
        <div className="relative sm:w-56">
          <Hash size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ardoise/50" />
          <input value={sku} onChange={(e) => setSku(e.target.value)} placeholder="SKU exact (optionnel)"
            className="w-full border border-dore/30 pl-9 pr-4 py-3 text-sm outline-none focus:border-dore" data-testid="cj-search-sku" />
        </div>
        <button disabled={busy || countdown !== null} className="bg-noir text-white px-6 py-3 text-xs uppercase tracking-widest disabled:opacity-50 flex items-center justify-center gap-2" data-testid="cj-search-btn">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} Rechercher
        </button>
      </form>
      <p className="text-[11px] text-ardoise/50 mb-4">Astuce : renseignez le <strong>SKU exact</strong> d'un produit CJ pour l'importer directement, ou un mot-clé pour explorer le catalogue.</p>

      {countdown !== null && (
        <div className="border border-dore/40 bg-dore/10 text-encre text-sm p-4 mb-6 flex items-center justify-between gap-3" data-testid="cj-retry-banner">
          <span className="flex items-center gap-2">
            <Clock size={16} className="text-dore animate-pulse" />
            Trop de recherches simultanées sur le réseau partagé. Nouvelle tentative automatique dans <strong>{countdown}s</strong>…
          </span>
          <button onClick={cancelRetry} className="flex items-center gap-1 text-xs text-ardoise hover:text-encre" data-testid="cj-retry-cancel">
            <X size={14} /> Annuler
          </button>
        </div>
      )}

      {err && (
        <div className="border border-red-300 bg-red-50 text-red-700 text-sm p-4 mb-6" data-testid="cj-search-error">{err}</div>
      )}

      {busy && countdown === null && <p className="text-ardoise text-sm">Recherche en cours...</p>}

      {data && !busy && (
        <>
          <p className="text-xs text-ardoise/70 mb-4 flex items-center gap-2" data-testid="cj-results-count">
            {data.total} résultat(s)
            {fromCache && <span className="text-[10px] uppercase tracking-wider bg-dore/15 text-encre px-2 py-0.5">en cache</span>}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="cj-results-grid">
            {data.products.map((p) => (
              <div key={p.pid} className="border border-dore/20 bg-white flex flex-col" data-testid="cj-result-card">
                <div className="aspect-square overflow-hidden bg-rose/10">
                  {p.image ? <img src={p.image} alt={p.name_en} className="w-full h-full object-cover" loading="lazy" /> : null}
                </div>
                <div className="p-3 flex flex-col flex-1">
                  <p className="text-xs text-encre line-clamp-2 mb-1 min-h-[2rem]">{p.name_en}</p>
                  <p className="text-[11px] text-ardoise/60 mb-3">{p.category_name}</p>
                  <div className="mt-auto">
                    {p.already_imported ? (
                      <span className="flex items-center justify-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 py-2" data-testid="cj-imported-badge">
                        <Check size={13} /> Déjà importé
                      </span>
                    ) : (
                      <button onClick={() => goImport(p)} data-testid="cj-import-btn"
                        className="w-full flex items-center justify-center gap-1.5 bg-dore/90 hover:bg-dore text-noir py-2 text-xs uppercase tracking-wider transition-colors">
                        <Download size={13} /> Importer
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {data.products.length === 0 && <p className="text-ardoise text-sm">Aucun produit trouvé.</p>}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-8" data-testid="cj-pagination">
              <button disabled={page <= 1} onClick={() => run(page - 1)} className="p-2 border border-dore/30 disabled:opacity-30" data-testid="cj-prev-page"><ChevronLeft size={16} /></button>
              <span className="text-sm text-encre">Page {page} / {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => run(page + 1)} className="p-2 border border-dore/30 disabled:opacity-30" data-testid="cj-next-page"><ChevronRight size={16} /></button>
            </div>
          )}
        </>
      )}

      {!data && !busy && !err && countdown === null && (
        <p className="text-ardoise text-sm" data-testid="cj-empty-hint">Lancez une recherche pour explorer le catalogue CJdropshipping.</p>
      )}
    </AdminShell>
  );
}
